import * as THREE from 'three';
import { interiorFloor } from '../state/interiorFloor';

// The surfaces a room says are for hanging things on.
//
// Free placement — aim the crosshair at a wall and the ghost snaps to whatever
// it hits — works indoors exactly as it does on the street, and it is the right
// tool for a room nobody designed for ads. But for a room WE authored, aiming is
// re-deriving something the builder already knew: scripts/make_salon.mjs put the
// accent wall, the retail panel and the sign board where they are ON PURPOSE, and
// it knows their centre, their facing and their size to the millimetre.
//
// So it writes that down. Every such mesh carries `affordance: 'postable'` in its
// glTF node extras (three's GLTFExporter serialises userData → extras, and
// GLTFLoader reads it straight back into userData), and this reads them off the
// loaded room. One click puts an ad dead-centre on a designed surface instead of
// wherever the crosshair happened to be.
//
// AUTHORING CONTRACT, since it cannot be inferred from geometry: a postable mesh
// is axis-aligned and unrotated, and `facing` is the yaw of its outward normal in
// ROOM space — 0 faces +z, π faces −z, −π/2 faces −x. `size` is [width, height]
// in world units. The room's own transform (fit, modelRotY, origin) is applied
// here, so an authored surface stays correct wherever the room ends up standing.

const FWD = new THREE.Vector3(0, 0, 1);
const UP = new THREE.Vector3(0, 1, 0);

export function listPostable() {
  const root = interiorFloor.object;
  if (!root) return [];
  root.updateMatrixWorld(true);

  const out = [];
  root.traverse((o) => {
    if (o.userData?.affordance !== 'postable') return;

    // Centre in world space — the room may be fitted, nudged or rotated, and all
    // of that is already baked into matrixWorld.
    const centre = new THREE.Vector3();
    o.getWorldPosition(centre);

    // Outward normal: the authored yaw, carried through the room's transform.
    const facing = typeof o.userData.facing === 'number' ? o.userData.facing : 0;
    const normal = FWD.clone()
      .applyQuaternion(new THREE.Quaternion().setFromAxisAngle(UP, facing))
      .transformDirection(o.matrixWorld)
      .normalize();

    // PlacementLayer's ghost builds its quaternion the same way — from +z to the
    // hit normal — so a surface placed from this list is indistinguishable from
    // one aimed at by hand.
    const quat = new THREE.Quaternion().setFromUnitVectors(FWD, normal);
    const pos = centre.clone().addScaledVector(normal, 0.02); // off the wall, no z-fighting

    const size = Array.isArray(o.userData.size) ? o.userData.size : null;
    out.push({
      name: o.name || 'postable',
      pos: [pos.x, pos.y, pos.z],
      quat: [quat.x, quat.y, quat.z, quat.w],
      // What will actually fit, with a margin so an ad does not run to the edge.
      width: size ? Math.max(0.3, size[0] * 0.8) : 2,
      height: size ? size[1] : null,
      surfaceSize: size,
    });
  });
  return out;
}
